import client from './client'
import {InterviewConfig, SegmentTranscript, SessionInfo, TranscribeJob} from '@/types/interview'

// ── Admin ────────────────────────────────────────────────────────────────────

export interface VideoSegment {
  file_url: string
  file_size: number
}

export interface SessionSummary {
  id: string
  job_title: string
  company_name: string
  status: string
  language: string
  created_at: string
  video_segments: VideoSegment[]
  transcripts: SegmentTranscript[]
}

export interface SessionTranscriptEntry {
  speaker: string
  content: string
  timestamp: string | null
}

export interface SessionSection {
  key: string
  label: string
  index: number
  user_video_url: string | null
  avatar_video_url: string | null
  transcripts: SessionTranscriptEntry[]
}

export interface SessionCandidate {
  first_name: string
  last_name: string
  email: string
}

export interface AdminSessionDetail {
  id: string
  job_title: string
  company_name: string
  status: string
  language: string
  avatar_gender: string
  created_at: string
  applicant_id: string
  candidate: SessionCandidate
  sections: SessionSection[]
}

export interface PaginatedResponse<T> {
  total: number
  items: T[]
}

export async function getSessions(
  filter:string="",
  order:string[]=[],
  skip = 0,
  take = 10
): Promise<PaginatedResponse<SessionSummary>> {
  const { data } = await client.post('/sessions/list', {
    pagination: { limit: take, offset: skip },
    filter,order
  })
  return { total: data.pagination.total, items: data.results }
}

export async function getAdminSession(id: string): Promise<AdminSessionDetail> {
  const { data } = await client.post('/admin/sessions/view', {
    filter: `Q(id='${id}')`,
  })
  return data.result
}

export function getLargestVideo(segments: VideoSegment[]): string | null {
  if (segments.length === 0) return null
  return segments.reduce((a, b) => (b.file_size > a.file_size ? b : a)).file_url
}

// ── Session lifecycle ────────────────────────────────────────────────────────

export async function createSession(config: InterviewConfig): Promise<{ session_id: string; session_token?: string }> {
  const { data } = await client.post('/sessions/create', {
    data: {
      config: {
        questions: [config.question1 ?? '', config.question2 ?? ''].filter(Boolean),
        enable_video: true,
        applicant_id: '',
      },
      avatar_gender: config.avatarGender,
      language: config.language,
    },
  })
  return data.result
}

export async function getSessionInfo(sessionId: string): Promise<SessionInfo> {
  const { data } = await client.post('/sessions/info', {
    data: { id: sessionId },
  })
  const result = data.result ?? {}
  const conn = result.livekit_connection ?? {}
  return {
    session_id: result.session_id ?? sessionId,
    livekit_url: conn.url ?? result.livekit_url ?? '',
    livekit_token: conn.access_token ?? result.access_token ?? '',
    // avatar_enabled = server-side AVATAR_ENABLED flag (whether BEY avatar service is configured)
    // NOT enable_video (which is the client config flag)
    avatar_enabled: result.avatar_enabled ?? false,
    total_questions: result.total_questions as number | undefined,
  }
}

export async function startSession(sessionId: string): Promise<void> {
  await client.post('/sessions/start', { data: { id: sessionId } })
}

export async function sendToSession(sessionId: string, text: string): Promise<void> {
  await client.post('/sessions/send', { data: { id: sessionId, text } })
}

export async function sendTTSToSession(sessionId: string, text: string, segment?: string): Promise<void> {
  await client.post('/sessions/send-with-tts', { data: { id: sessionId, text, segment } })
}

export async function closeSession(sessionId: string): Promise<void> {
  await client.post('/sessions/close', { data: { id: sessionId } })
}

export async function finalizeSession(sessionId: string): Promise<void> {
  await client.post('/sessions/finalize', { data: { id: sessionId } })
}

export async function segmentStart(sessionId: string, segment: string): Promise<void> {
  await client.post('/sessions/segment/start', { data: { id: sessionId, segment } })
}

export async function segmentEnd(sessionId: string, segment: string): Promise<void> {
  await client.post('/sessions/segment/end', { data: { id: sessionId, segment } })
}

export async function submitEmail(
  sessionId: string,
  payload: { first_name: string; last_name: string; email: string }
): Promise<void> {
  await client.post(`/sessions/${sessionId}/email/`, payload)
}

// ── Video & transcription ────────────────────────────────────────────────────

export async function uploadVideo(blob: Blob, sessionId: string, segment: string): Promise<void> {
  const formData = new FormData()
  formData.append('video', blob, `${sessionId}_${segment}.webm`)
  formData.append('session_id', sessionId)
  formData.append('segment', segment)
  await client.post('/interview/video/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export async function saveSegmentMetadata(payload: {
  session_id: string
  segment: string
  start_time: number
  end_time: number
  language: string
}): Promise<void> {
  await client.post('/interview/segment/metadata/', payload)
}

export async function startTranscription(
  videoBlob: Blob,
  sessionId: string,
  segment: string,
  language: string,
  startTime: number,
  endTime: number
): Promise<TranscribeJob> {
  const { data } = await client.post<TranscribeJob>('/transcribe/', videoBlob, {
    params: {
      language: language === 'de' ? 'de-DE' : 'en-US',
      segment,
      session_id: sessionId,
      start_time: String(startTime),
      end_time: String(endTime),
    },
    headers: { 'Content-Type': 'application/octet-stream' },
  })
  return data
}

export async function getTranscriptionResult(jobId: string): Promise<{
  status: 'pending' | 'done' | 'error'
  text?: string
}> {
  const { data } = await client.get(`/transcribe/${jobId}/`)
  return data
}
